import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AcademicCapIcon,
  ChartBarIcon,
  ShieldExclamationIcon,
  EyeIcon,
  PlusIcon
} from "@heroicons/react/24/outline";
import ModuleDetails from "./ModuleDetails";
import TabNavigation from "../ui/TabNavigation";
import ApiLoader from "../ui/ApiLoader";
import PageLoader from "../ui/PageLoader";

import QuizComponent from "./QuizComponent";
import { DataTable } from "../common/DataTable";
import { QuizAnalyticsProvider } from "./QuizAnalyticsContext";
import AiLmsFormModal from "./AiLmsFormModal";
import LmsUploadModal from "./LmsUploadModal";
import toast from "../../utils/notify";

import {
  Play,
  CheckCircle2,
  Clock,
  FileQuestion,
  SendIcon,
  Train
} from "lucide-react";

import { Link } from "react-router-dom";
import axios from "@/services/axios";
import { useAuth } from "@/context/AuthContext";
import { TrainingCatalog } from "./TrainingCatalog";
import { useSelector } from "react-redux";
import { getLMSCustomerModulesApi, getLMSEndUserModulesApi, getQuizQuestionsApi, createLMSModuleApi, updateLMSModuleApi } from "../../services/axios/AuthApis/AiLMSApis";

const Ai_LMS_Content = () => {
  const { user, loading: authLoading, hasModulePermission } = useAuth();

  const [activeTab, setActiveTab] = useState("training_catalog");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [watchVideoEnabled, setWatchVideoEnabled] = useState(false);
  const [quizEnabled, setQuizEnabled] = useState(false);
  const [quizModuleId, setQuizModuleId] = useState(null);
  const [quizList, setQuizList] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [showLmsModal, setShowLmsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const userDetail = useSelector((state) => state.user);
  const customerMasterId = userDetail?.user?.customer_master_id || "";
  const userRole = userDetail?.user?.role || '';
  const roleKey = (userRole || "").toLowerCase().replace(/\s+/g, "_");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    total_count: 0,
  });
  const [quizPagination, setQuizPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    total_count: 0,
  });
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const restrictedRoles = ["customer_admin", "security_analyst", "auditor"];
  const restrictedTabs = ["watch_video", "quiz"];

  const LMS_TABS = [
    { id: "training_catalog", name: "Training Catalog", icon: AcademicCapIcon },
    { id: "watch_video", name: "Watch Video", icon: EyeIcon },
    { id: "quiz", name: "Quiz list", icon: ChartBarIcon },
    // { id: "phishing_simulations", name: "Phishing Simulations", icon: ShieldExclamationIcon }
  ].filter(tab => {
    // If user is customer_admin, hide watch_video and quiz
    if (restrictedRoles.includes(roleKey) && restrictedTabs.includes(tab.id)) {
      return false;
    }
    return true;
  });


  /* ------------------------------------------------------------------
     Fetch LMS Modules
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (authLoading) return;
    if (!hasModulePermission("learning-management")) return;

    if (activeTab !== "training_catalog") return;

    fetchModules();
  }, [activeTab, authLoading, pagination.page]);


  const fetchModules = async () => {
    try {
      setLoading(true);
      let res;

      console.log("Fetching LMS modules for role:", userRole);

      // 🔴 STOP if end_user but employee_id not ready
      if (roleKey === "end_user" && !userDetail?.user?.id) {
        console.warn("Employee ID not available yet, skipping fetch");
        return;
      }

      if (roleKey === "admin" || roleKey === "platform_admin") {
        res = await axios.get("/api/lms/modules/", {
          params: {
            page: pagination.page,
            page_size: pagination.page_size,
          },
        });
      }

      else if (roleKey === "customer_admin") {
        res = await getLMSCustomerModulesApi(
          pagination.page,
          pagination.page_size,
          userDetail.user.id
        );
      }
      else if (roleKey === "security_analyst" && selectedCompany) {
        res = await getLMSCustomerModulesApi(
          pagination.page,
          pagination.page_size,
          selectedCompany.user_id
        );
      }
      else if (roleKey === "auditor" && selectedCompany) {
        res = await getLMSCustomerModulesApi(
          pagination.page,
          pagination.page_size,
          selectedCompany.user_id
        );
      }

      else if (roleKey === "end_user") {

        // 🔥 employee_id = logged-in user's id
        const employeeId = userDetail?.user?.id;

        if (!employeeId) {
          console.warn("Employee ID not available yet, skipping fetch");
          return;
        }

        res = await getLMSEndUserModulesApi(employeeId);
      }

      if (!res?.data) {
        setModules([]);
        return;
      }

      const data = res.data;

      const results = Array.isArray(data?.results)
        ? data.results
        : [];

      setModules(
        results.map((m) => {
          let qCount = m.quizQuestions;

          if (roleKey === "customer_admin" || roleKey === "security_analyst" || roleKey === "auditor") {
            qCount = 10;
          } else if (roleKey === "admin" || roleKey === "platform_admin") {
            qCount = 100;
          }
          return {
            id: m._id || m.id,
            title: m.moduleTitle || "",
            description: m.description || "",
            duration: m.duration || "45 min",
            status: (m.status || "available").toLowerCase(),
            progress: m.progress || 0,
            quizQuestions: qCount,
            isoAlignment: m.isoAlignment || ["ISO 27001"],
            icon: AcademicCapIcon,
            video_seen: m.video_seen || false,
            quiz_result: m.quiz_result || null,
          };
        })
      );

      // Update pagination info from API response
      if (roleKey === "admin" || roleKey === "platform_admin") {
        setPagination((prev) => ({
          ...prev,
          total_pages: data?.total_pages || 1,
          total_count: data?.total || results.length,
        }));
      }

    } catch (error) {
      console.error("Failed to fetch LMS modules", error);
      setModules([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (roleKey === "end_user") {
      if (userDetail?.user?.id) {
        fetchModules();
      }
    } else {
      fetchModules();
    }
  }, [roleKey, userDetail]);

  // Lock Watch Video again whenever user returns to Training Catalog.
  useEffect(() => {
    if (activeTab === "training_catalog") {
      setWatchVideoEnabled(false);
    }
  }, [activeTab]);


  const handleSaveModule = async (formData) => {
    try {
      setFormSaving(true);
      
      if (editingModule) {
        // Update existing module
        await updateLMSModuleApi(editingModule.id, formData);
        toast.success('Module updated successfully');
      } else {
        // Create new module
        await createLMSModuleApi(formData);
        toast.success('Module created successfully');
      }
      
      setShowLmsModal(false);
      setEditingModule(null);
      // Refresh modules list
      fetchModules();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Error saving module: ' + (error.response?.data?.message || error.message));
    } finally {
      setFormSaving(false);
    }
  };


  const fetchQuizForModule = async (moduleId, page = 1, pageSize = quizPagination.page_size) => {
    try {
      setQuizLoading(true);

      const quizRole = roleKey === "platform_admin" ? "admin" : roleKey;
      console.log({quizPagination})
      const data = await getQuizQuestionsApi({
        moduleId,
        page,
        pageSize,
        role: quizRole,
        employeeId: userDetail?.user?.id
      });

      setQuizList(data.results || []);

      setQuizPagination((prev) => ({
        ...prev,
        page,
        page_size: pageSize,
        total_pages: data.total_pages,
        total_count: data.count ?? prev.total_count,
      }));

    } catch (err) {
      console.error("Failed to load quiz", err);
      setQuizList([]);
    } finally {
      setQuizLoading(false);
    }
  };



  const statusConfig = {
    available: {
      badge: "Available",
      badgeClass: "bg-info/10 text-info border-info/20",
      buttonText: "Start Module"
    },
    "in-progress": {
      badge: "In Progress",
      badgeClass: "bg-warning/10 text-warning border-warning/20",
      buttonText: "Continue"
    },
    completed: {
      badge: "Completed",
      badgeClass: "bg-success/10 text-success border-success/20",
      buttonText: "Review"
    }
  };

  /* ------------------------------------------------------------------
     RBAC: Block entire page if no LMS access
  ------------------------------------------------------------------ */
  if (authLoading || initialLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading AI LMS..." minH="min-h-screen" />
        </div>
      </div>
    );
  }

  if (!hasModulePermission("learning-management")) {
    return (
      <div className="text-center py-20 text-gray-500">
        You do not have permission to access Learning Management.
      </div>
    );
  }

  /* ------------------------------------------------------------------
     UI
  ------------------------------------------------------------------ */
  return (


    <div className="p-3 sm:p-4 md:p-6 max-w-full mx-auto min-h-screen">      {/* Header */}


      <div className="mb-6 md:mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <AcademicCapIcon className="w-6 md:w-8 h-6 md:h-8 text-blue-600" />
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900">
              AI LMS
            </h1>
            <p className="text-xs md:text-sm text-gray-600">
              The learning management system concept emerged directly from e-Learning
            </p>
          </div>
        </div>



        {/* Tabs */}
        <TabNavigation
          tabs={LMS_TABS.map((tab) => {
            const isLockedDuringWatchVideo =
              activeTab === "watch_video" &&
              (tab.id === "training_catalog" || tab.id === "quiz");

            const isLockedDuringQuiz =
              activeTab === "quiz" && tab.id === "training_catalog";

            const isDisabled =
              isLockedDuringQuiz ||
              isLockedDuringWatchVideo ||
              (tab.id === "watch_video" && !watchVideoEnabled) ||
              (tab.id === "quiz" && !quizEnabled);

            return { ...tab, disabled: isDisabled };
          })}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === "training_catalog" && (
          <TrainingCatalog
            loading={loading}
            modules={modules}
            setShowLmsModal={setShowLmsModal}
            setShowUploadModal={setShowUploadModal}
            setEditingModule={setEditingModule}
            setModuleLoading={setModuleLoading}
            setActiveTab={setActiveTab}
            setWatchVideoEnabled={setWatchVideoEnabled}
            selectedModuleId={selectedModuleId}
            setSelectedModuleId={setSelectedModuleId}
            pagination={pagination}
            setPagination={setPagination}
            fetchModules={fetchModules}
          />
        )}


        {activeTab === "watch_video" && (
          <motion.div
            key="watch_video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {!selectedModuleId ? (
              <div className="text-center py-20 text-gray-500">
                Select a module to start
              </div>
            ) : (
              <div className="relative">
                {moduleLoading && (
                  <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/70">
                    <ApiLoader label="Loading module content..." className="py-0" />
                  </div>
                )}

                <ModuleDetails
                  moduleId={selectedModuleId}
                  employeeId={userDetail?.user?.id}   // ✅ PASS IT HERE
                  userRole={userRole}
                  onBack={() => {
                    setSelectedModuleId(null);
                    setWatchVideoEnabled(false);
                    setActiveTab("training_catalog");
                  }}
                  onMoveQuiz={(moduleId) => {
                    if (roleKey !== "customer_admin" && roleKey !== "security_analyst" && roleKey !== "auditor") {
                      setQuizEnabled(true);
                      setQuizModuleId(moduleId);
                      setQuizPagination((p) => ({ ...p, page: 1 }));
                      setActiveTab("quiz");
                      fetchQuizForModule(moduleId, 1);
                    } else {
                      setActiveTab("training_catalog");
                    }
                  }}
                  onLoaded={() => setModuleLoading(false)}
                />
              </div>
            )}

          </motion.div>
        )}

        {activeTab === "quiz" && roleKey !== "customer_admin" && roleKey !== "security_analyst" && roleKey !== "auditor" && (
          <motion.div
            key="quiz"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {quizLoading ? (
              <ApiLoader label="Loading quiz..." className="py-20" />
            ) : roleKey === "end_user" ? (
              quizList.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                  No quiz available for this module.
                </div>
              ) : (
                <QuizComponent
                  questions={quizList.map((q) => ({
                    id: q._id,
                    question: q.question,
                    options: q.options,
                    correctAnswer: q.correct_answer,
                  }))}
                  passingScore={70}
                  onComplete={(score, passed) => {
                    console.log("Quiz completed:", score, passed);
                  }}
                  isUnlocked={true}
                  employeeId={userDetail?.user?.id}      // 🔥 ADD
                  lmsId={quizModuleId}
                  moduleTitle={modules.find((m) => m.id === quizModuleId)?.title || ""}
                  userName={userDetail?.user?.firstname || "Employee"}
                />
              )
            ) : (
              <DataTable
                columns={[
                  {
                    id: "sno",
                    header: "S.No",
                    enableSorting: false,
                    cell: ({ row }) => <span>{row.index + 1}</span>,
                  },
                  {
                    accessorKey: "question",
                    header: "Question",
                  },
                  {
                    id: "answer",
                    header: "Answer",
                    cell: ({ row }) => {
                      const q = row.original;
                      const answerText = q.options?.includes(q.correct_answer)
                        ? q.correct_answer
                        : q.options?.[q.correct_answer] || "Answer not found";
                      return <span className="font-semibold">{answerText}</span>;
                    },
                  },
                ]}
                data={quizList}
                getRowId={(q, index) => String(q._id || index)}
                loading={quizLoading}
                emptyMessage="No quiz available for this module."
                manualPagination
                rowCount={quizPagination.total_count || 0}
                pagination={{ pageIndex: (quizPagination.page || 1) - 1, pageSize: quizPagination.page_size }}
                onPaginationChange={(updater) => {
                  const current = { pageIndex: (quizPagination.page || 1) - 1, pageSize: quizPagination.page_size };
                  const next = typeof updater === "function" ? updater(current) : updater;
                  fetchQuizForModule(quizModuleId, next.pageIndex + 1, next.pageSize);
                }}
                enableColumnVisibility={false}
                maxBodyHeight="65vh"
              />
            )}
          </motion.div>
        )}

      </AnimatePresence>

      {/* LMS Form Modal */}
      {(showLmsModal || editingModule) && (
        <AiLmsFormModal
          record={editingModule}
          onClose={() => {
            setShowLmsModal(false);
            setEditingModule(null);
          }}
          onSave={handleSaveModule}
        />
      )}

      {/* LMS Upload Modal */}
      <LmsUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          setModuleLoading(true);
          fetchModules();
        }}
      />

    </div>
  );
};

const Ai_LMS = () => {
  return (
    <QuizAnalyticsProvider>
      <Ai_LMS_Content />
    </QuizAnalyticsProvider>
  );
};

export default Ai_LMS;
